/**
 * Loads one Excentos Product Guide from CMS-provided data attributes. The vendor
 * runtime owns fixed, page-global #xc_* mount IDs, so only one guide is supported
 * per page.
 */

type ExcentosConfig = {
  baseUrl: string;
  application: string;
  locale: string;
  theme: string;
};

// Prevent duplicate script injection during repeated module mounts.
const scriptPromises = new Map<string, Promise<void>>();

function readConfig(root: HTMLElement): ExcentosConfig | null {
  const baseUrl = root.dataset.excentosBaseUrl?.trim().replace(/\/+$/, '');
  const application = root.dataset.excentosApplication?.trim();
  const locale = root.dataset.excentosLocale?.trim();
  const theme = root.dataset.excentosTheme?.trim();

  if (!baseUrl || !/^(?:https?:)?\/\//i.test(baseUrl) || !application || !locale) {
    return null;
  }

  return { baseUrl, application, locale, theme: theme ?? '' };
}

function buildScriptUrl(config: ExcentosConfig): string {
  const { baseUrl, application, locale, theme } = config;
  const path = [application, locale, 'loadadvisor']
    .map(segment => encodeURIComponent(segment))
    .join('/');
  const query = theme ? `?theme=${encodeURIComponent(theme)}` : '';

  return `${baseUrl}/${path}${query}`;
}

function setError(root: HTMLElement, message: string): void {
  const status = root.querySelector<HTMLElement>('[data-excentos-status]');
  if (!status) return;

  status.textContent = message;
  status.dataset.state = 'error';
  status.removeAttribute('hidden');
}

function showFallback(root: HTMLElement, message: string): void {
  root.dataset.excentosLoading = 'false';
  setError(root, message);
}

function markLoaded(root: HTMLElement): void {
  root.dataset.excentosLoaded = 'true';
  root.dataset.excentosLoading = 'false';
  const status = root.querySelector<HTMLElement>('[data-excentos-status]');
  status?.setAttribute('hidden', '');
}

function loadScript(src: string): Promise<void> {
  const existingPromise = scriptPromises.get(src);
  if (existingPromise) return existingPromise;

  const promise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"], script[data-excentos-script="${src}"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Unable to load excentos script')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.excentosScript = src;

    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Unable to load excentos script')), {
      once: true
    });

    document.body.appendChild(script);
  });

  scriptPromises.set(src, promise);

  return promise;
}

async function initExcentos(root: HTMLElement): Promise<void> {
  if (root.dataset.excentosLoaded === 'true' || root.dataset.excentosLoading === 'true') {
    return;
  }

  const config = readConfig(root);
  if (!config) {
    showFallback(
      root,
      root.dataset.excentosConfigErrorLabel ?? 'The product guide is not configured correctly.'
    );
    return;
  }

  if (!root.querySelector('#xc_application')) {
    showFallback(
      root,
      root.dataset.excentosLoadErrorLabel ??
        'The product guide could not be loaded. Please try again later.'
    );
    return;
  }

  try {
    root.dataset.excentosLoading = 'true';
    await loadScript(buildScriptUrl(config));
    markLoaded(root);
  } catch {
    showFallback(
      root,
      root.dataset.excentosLoadErrorLabel ??
        'The product guide could not be loaded. Please try again later.'
    );
  }
}

export default function excentosForm(root: HTMLElement): void {
  void initExcentos(root);
}
